import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';
import './App.css';

function StoreProducts() {
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();

  // Get selected store from sessionStorage
  const getSelectedStore = () => {
    try {
      const selectedStoreData = sessionStorage.getItem('selectedStore');
      return selectedStoreData ? JSON.parse(selectedStoreData) : null;
    } catch (error) {
      console.error('Error getting selected store:', error);
      return null;
    }
  };

  const selectedStore = getSelectedStore();
  const catalogId = selectedStore?.Group_CatalogId || null;

  // Page range states for flexible fetching
  const [productPageRange, setProductPageRange] = useState({ start: '1', end: '5' });
  const [originalProducts, setOriginalProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state for image preview
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState([]);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  // Row selection state for bulk operations
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Excel upload states
  const [excelFile, setExcelFile] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState('');
  const [excelData, setExcelData] = useState(null);
  const [uploadMode, setUploadMode] = useState('session'); // 'session' or 'excel'

  const fetchStoreProducts = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch products from store catalog - backend handles credentials internally
      const response = await axios.post('/api/store-products/fetch/', {}, {
        params: {
          catalog_id: catalogId,
          start_page: productPageRange.start,
          end_page: productPageRange.end
        }
      });

      if (response.data.success) {
        const cleanedData = response.data.data.filter(product => {
          const originalPrice = parseFloat(product.OriginalPrice);
          return product.StockAvailability === 'InStock' &&
                 originalPrice >= 5 &&
                 originalPrice <= 10000 &&
                 product.Category;
        });

        setOriginalProducts(cleanedData);
        setFilteredProducts(cleanedData);
        setTotalPages(Math.ceil(cleanedData.length / pageSize));
        setCurrentPage(1);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch store products data');
    }
    setLoading(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredProducts.length / newSize));
  };

  const handleCategorySearch = (searchValue) => {
    setCategorySearchTerm(searchValue);
    setCurrentPage(1);

    if (searchValue.trim() === '') {
      setFilteredProducts(originalProducts); // Restore original data when cleared
      setTotalPages(Math.ceil(originalProducts.length / pageSize));
    } else {
      const filtered = originalProducts.filter(product =>
        product.Category &&
        product.Category.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredProducts(filtered);
      setTotalPages(Math.ceil(filtered.length / pageSize));
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowSelection = (globalIndex) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(globalIndex)) {
      newSelectedRows.delete(globalIndex);
    } else {
      newSelectedRows.add(globalIndex);
    }
    setSelectedRows(newSelectedRows);

    // Update select all state based on current page
    updateSelectAllState(newSelectedRows);
  };

  const updateSelectAllState = (selectedSet = selectedRows) => {
    const currentPageData = getCurrentPageData();
    const currentPageGlobalIndices = currentPageData.map((_, index) =>
      (currentPage - 1) * pageSize + index
    );
    const allCurrentPageSelected = currentPageGlobalIndices.every(index => selectedSet.has(index));
    setSelectAll(allCurrentPageSelected);
  };

  const handleSelectAll = () => {
    const currentPageData = getCurrentPageData();
    const currentPageGlobalIndices = currentPageData.map((_, index) =>
      (currentPage - 1) * pageSize + index
    );

    const newSelectedRows = new Set(selectedRows);
    const allCurrentPageSelected = currentPageGlobalIndices.every(index => newSelectedRows.has(index));

    if (allCurrentPageSelected) {
      // Deselect all items on current page
      currentPageGlobalIndices.forEach(index => newSelectedRows.delete(index));
    } else {
      // Select all items on current page
      currentPageGlobalIndices.forEach(index => newSelectedRows.add(index));
    }

    setSelectedRows(newSelectedRows);
    setSelectAll(!allCurrentPageSelected);
  };

  // Update select all state when page changes
  React.useEffect(() => {
    updateSelectAllState();
  }, [currentPage, filteredProducts, pageSize]);

  const removeRow = (index) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    const updatedProducts = [...filteredProducts];
    updatedProducts.splice(globalIndex, 1);
    setFilteredProducts(updatedProducts);
    setTotalPages(Math.ceil(updatedProducts.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await axios.post('/api/products/download/', { products: filteredProducts }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'impact_products_filtered.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download CSV');
    }
  };

  // Image preview modal handlers
  const handleImageClick = (imageUrls, imageAlt) => {
    if (Array.isArray(imageUrls)) {
      setSelectedImage(imageUrls);
    } else {
      setSelectedImage([imageUrls]);
    }
    setSelectedImageAlt(imageAlt);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage([]);
    setSelectedImageAlt('');
  };

  const bulkRemove = () => {
    const indicesToRemove = Array.from(selectedRows).sort((a, b) => b - a);
    const updatedProducts = [...filteredProducts];

    indicesToRemove.forEach(index => {
      updatedProducts.splice(index, 1);
    });

    setFilteredProducts(updatedProducts);
    setSelectedRows(new Set());
    setSelectAll(false);
    setTotalPages(Math.ceil(updatedProducts.length / pageSize));

    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  const handleProductsUpload = () => {
    // Prepare products for upload table
    // Use selected products if any are selected, otherwise use all filtered products
    const productsToUpload = selectedRows.size > 0
      ? Array.from(selectedRows).map(index => filteredProducts[index]).filter(product => product)
      : filteredProducts;

    // Store products data in sessionStorage for upload page
    sessionStorage.setItem('productsUploadData', JSON.stringify(productsToUpload));
    sessionStorage.setItem('productsUploadSelected', JSON.stringify(Array.from(selectedRows)));

    // Navigate to products upload table
    navigate('/products-upload');
  };

  const handleAdsUpload = () => {
    // Prepare ads data from products for upload table
    // Use selected products if any are selected, otherwise use all filtered products
    const productsToProcess = selectedRows.size > 0
      ? Array.from(selectedRows).map(index => filteredProducts[index]).filter(product => product)
      : filteredProducts;

    // Store products data in sessionStorage for ads processing
    sessionStorage.setItem('adsFromProducts', JSON.stringify(productsToProcess));
    sessionStorage.setItem('adsFromProductsSelected', JSON.stringify(Array.from(selectedRows)));

    // Navigate to coupons upload page where ads can be processed
    navigate('/ads-upload');
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredProducts.slice(startIndex, endIndex);
  };

  // Excel upload functions
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type)) {
        setExcelError('Please select a valid Excel file (.xlsx or .xls)');
        setExcelFile(null);
        return;
      }
      setExcelFile(file);
      setExcelError('');
      setExcelData(null); // Clear previous data
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      setExcelError('Please select an Excel file first');
      return;
    }

    // Clear previous messages
    setError('');
    setExcelError('');

    setExcelLoading(true);

    try {
      console.log('📊 EXCEL STORE PRODUCTS UPLOAD: Starting Excel file upload');

      const formData = new FormData();
      formData.append('excel_file', excelFile);

      const response = await axios.post('/api/store-products/upload-excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout for file upload
      });

      console.log('📊 EXCEL STORE PRODUCTS UPLOAD: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ EXCEL STORE PRODUCTS UPLOAD: Successfully parsed', response.data.valid_products, 'products');

        // Set the parsed data
        setExcelData(response.data.data);
        setOriginalProducts(response.data.data);
        setFilteredProducts(response.data.data);
        setUploadMode('excel');
        setTotalPages(Math.ceil(response.data.data.length / pageSize));
        setCurrentPage(1);

        // Show success message
        // Add success message display logic if needed

        // Show validation errors if any
        if (response.data.validation_errors && response.data.validation_errors.length > 0) {
          const errorMsg = `⚠️ ${response.data.validation_errors.length} validation errors found. Check console for details.`;
          console.warn('📊 EXCEL STORE PRODUCTS UPLOAD: Validation errors:', response.data.validation_errors);
          setExcelError(errorMsg);
        }

      } else {
        console.error('❌ EXCEL STORE PRODUCTS UPLOAD: Upload failed:', response.data.error);
        setExcelError(response.data.error);
      }

    } catch (err) {
      console.error('❌ EXCEL STORE PRODUCTS UPLOAD: Error during Excel upload:', err);

      let errorMessage = 'Failed to upload Excel file';

      if (err.response) {
        console.error('❌ EXCEL STORE PRODUCTS UPLOAD: Response status:', err.response.status);
        console.error('❌ EXCEL STORE PRODUCTS UPLOAD: Response data:', err.response.data);

        if (err.response.data && err.response.data.error) {
          errorMessage = `Server error: ${err.response.data.error}`;
        } else {
          errorMessage = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        console.error('❌ EXCEL STORE PRODUCTS UPLOAD: No response received:', err.request);
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        console.error('❌ EXCEL STORE PRODUCTS UPLOAD: Request setup error:', err.message);
        errorMessage = 'Error setting up request: ' + err.message;
      }

      setExcelError(errorMessage);
    }

    setExcelLoading(false);
  };

  const switchToSessionData = () => {
    try {
      // For store products, we don't have persistent session data like coupons/banners
      // This switches to the fetched API data if available, otherwise clears to empty
      if (filteredProducts.length > 0) {
        setUploadMode('session');
        setCurrentPage(1);
        setExcelData(null);
        setExcelFile(null);
        setExcelError('');
        // Success message could be added here if needed
      } else {
        setError('No session product data available');
      }
    } catch (err) {
      console.error('Error switching to session data:', err);
      setError('Error switching to session product data');
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header with Perzsi Branding */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Store Products - {selectedStore?.Name || 'Unknown Store'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Store Catalog Products</p>
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        {/* Control Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Fetch Store Products</h3>
            <p className={`mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Fetch products for this store using the Impact API Catalogs endpoint.
            </p>
            <div className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center space-x-3 mb-2">
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Start Page:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={productPageRange.start}
                    onChange={(e) => setProductPageRange({...productPageRange, start: e.target.value})}
                    className="perzsi-input text-center w-16"
                    placeholder="1"
                  />
                </div>
                <span className="text-gray-400">to</span>
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    End Page:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={productPageRange.end}
                    onChange={(e) => setProductPageRange({...productPageRange, end: e.target.value})}
                    className="perzsi-input text-center w-16"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>
            <button
              className="perzsi-button-primary w-full"
              onClick={fetchStoreProducts}
              disabled={loading || !catalogId}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Fetching Products...
                </span>
              ) : !catalogId ? (
                'Invalid Catalog ID'
              ) : (
                `Fetch Products (${productPageRange.start}-${productPageRange.end})`
              )}
            </button>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Store Information</h3>
            {selectedStore ? (
              <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <p><strong>Store Name:</strong> {selectedStore.Name}</p>
                <p><strong>Store ID:</strong> {selectedStore.Id}</p>
                <p><strong>Catalog ID:</strong> {selectedStore.Group_CatalogId || 'N/A'}</p>
                <p><strong>Group Name:</strong> {selectedStore.Group_Name || 'N/A'}</p>
                <p><strong>Last Updated:</strong> {selectedStore.DateLastUpdated}</p>
              </div>
            ) : (
              <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>No store selected. Please go back and select a store.</p>
            )}
            <div className="mt-4">
              <button
                className="perzsi-button-secondary w-full"
                onClick={() => navigate('/stores')}
              >
                ← Back to Stores
              </button>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Pagination Settings</h3>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Products per page:
              </label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="perzsi-input"
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={300}>300</option>
                <option value={400}>400</option>
                <option value={500}>500</option>
                <option value={750}>750</option>
                <option value={1000}>1000</option>
              </select>
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Search by Category:
              </label>
              <input
                type="text"
                value={categorySearchTerm}
                onChange={(e) => handleCategorySearch(e.target.value)}
                placeholder="Search categories..."
                className="perzsi-input"
              />
            </div>
            {filteredProducts.length > 0 && (
              <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <p><strong>Total Products: {filteredProducts.length}</strong></p>
                <p><strong>Showing: {getCurrentPageData().length} of {filteredProducts.length}</strong></p>
              </div>
            )}
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Excel File Upload</h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Upload store products data from Excel file. <br/>
              <strong>All columns are required:</strong> Id, Name, ImageUrl, CurrentPrice, OriginalPrice, Category, Url, CampaignName, Description, Manufacturer, DiscountPercentage, Currency, StockAvailability, AdditionalImageUrls, SubCategory, Condition, Mpn, Gtin, Colors, Size, LaunchDate, ExpirationDate <br/>
              Supports .xlsx and .xls formats.
            </p>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Excel File:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-perzsi-yellow file:text-perzsi-gray hover:file:bg-yellow-400"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  className="perzsi-button-primary flex-1"
                  onClick={handleExcelUpload}
                  disabled={excelLoading || !excelFile}
                >
                  {excelLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Parsing...
                    </span>
                  ) : (
                    'Parse Excel File'
                  )}
                </button>

                {uploadMode === 'excel' && (
                  <button
                    className="perzsi-button-secondary"
                    onClick={switchToSessionData}
                  >
                    Switch to Session Data
                  </button>
                )}
              </div>

              {excelError && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">❌</span>
                    <span className="text-sm font-medium">{excelError}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        {filteredProducts.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-6 justify-between">
            <div className="flex flex-wrap gap-4">
              <button
                className="perzsi-button-primary"
                onClick={downloadCSV}
              >
                Download Filtered Products as CSV
              </button>
              <button
                className="perzsi-button-secondary"
                onClick={handleProductsUpload}
                disabled={filteredProducts.length === 0}
              >
                Products Upload Table
              </button>
              <button
                className="perzsi-button-secondary "
                onClick={handleAdsUpload}
                
              >
                Ads Upload Table
              </button>
              {selectedRows.size > 0 && (
                <button
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                  onClick={bulkRemove}
                >
                  Remove Bulk Items ({selectedRows.size})
                </button>
              )}
            </div>
          </div>
        )}

        {/* Top Pagination */}
        {filteredProducts.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center mb-4">
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {(() => {
              let items = [];
              const maxVisiblePages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              for (let number = startPage; number <= endPage; number++) {
                items.push(
                  <button
                    key={number}
                    className={`mx-1 px-3 py-1 rounded ${
                      number === currentPage
                        ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
                        : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handlePageChange(number)}
                  >
                    {number}
                  </button>
                );
              }
              return items;
            })()}
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        )}

        {/* Spacer */}
        <div className="mb-4"></div>

        {/* Products Table */}
        {filteredProducts.length > 0 && (
          <div className="perzsi-table-container">
            <div className="overflow-x-auto overflow-y-hidden">
              <table className="perzsi-table">
                <thead>
                  <tr>
                    <th className="w-16">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                      />
                    </th>
                    <th>Id</th>
                    <th>Campaign Name</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Url</th>
                    <th>Image</th>
                    <th>Additional Images</th>
                    <th>Current Price</th>
                    <th>Original Price</th>
                    <th>Gtin</th>
                    <th>Mpn</th>
                    <th>Category</th>
                    <th>Colors</th>
                    <th>Size</th>
                    <th>Launch Date</th>
                    <th>Expiration Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageData().map((product, index) => {
                    const globalIndex = (currentPage - 1) * pageSize + index;

                    return (
                      <tr key={globalIndex} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.has(globalIndex)}
                            onChange={() => handleRowSelection(globalIndex)}
                            className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                          />
                        </td>
                        <td>{product.Id}</td>
                        <td>{product.CampaignName}</td>
                        <td className="font-medium max-w-xs truncate" title={product.Name}>
                          {product.Name}
                        </td>
                        <td className="max-w-xs truncate" title={product.Description}>{product.Description}</td>
                        <td className="max-w-xs">
                          {product.Url && (
                            <div>
                              <a
                                href={product.Url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                              >
                                View Product
                              </a>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="ads-image-container">
                            {product.ImageUrl && (
                              <img
                                src={product.ImageUrl}
                                alt={product.Name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                                onClick={() => handleImageClick(product.ImageUrl, product.Name)}
                                style={{
                                  maxWidth: '100px',
                                  maxHeight: '50px',
                                  marginRight: '5px',
                                  cursor: 'pointer',
                                  border: '2px solid transparent',
                                  borderRadius: '4px'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.border = '2px solid #FFD700';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.border = '2px solid transparent';
                                }}
                              />
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="relative w-24 h-12 cursor-pointer" onClick={() => handleImageClick(product.AdditionalImageUrls?.slice(0, 5), product.Name)}>
                            {product.AdditionalImageUrls && product.AdditionalImageUrls.slice(0, 5).map((url, i) => (
                              <img key={i} src={url} alt="Additional product image" className="absolute w-10 h-10 object-cover rounded-md border-2 border-white" style={{ top: `${i * 0.5}rem`, left: `${i * 0.5}rem` }} />
                            ))}
                          </div>
                        </td>
                        <td className="font-semibold text-green-600">
                          {product.CurrentPrice}
                        </td>
                        <td>{product.OriginalPrice}</td>
                        <td>{product.Gtin}</td>
                        <td>{product.Mpn}</td>
                        <td className="max-w-xs truncate" title={product.Category}>
                          {product.Category && product.Category.split('>').slice(0, 3).join(' > ')}
                        </td>
                        <td>{product.Colors && product.Colors.join(', ')}</td>
                        <td>{product.Size}</td>
                        <td>{product.LaunchDate}</td>
                        <td>{product.ExpirationDate}</td>
                        <td>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                            onClick={() => removeRow(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/stores')}
          >
            ← Back to Stores
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-perzsi-gray">Product Image Preview</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="text-center">
                {selectedImage.map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt={`${selectedImageAlt} - ${index + 1}`}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg mb-4"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="perzsi-button-primary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StoreProducts;
